export function isMenoraContents(entries) {
  return entries.some(f => f.name.toUpperCase().endsWith('P.TXT')) &&
         entries.some(f => f.name.toUpperCase().endsWith('N.TXT'));
}

// CP862 (IBM Hebrew DOS) byte → Unicode
const CP862 = {};
const HEB_LETTERS = "אבגדהוזחטיךכלםמןנסעףפץצקרשת";
for (let i = 0; i < HEB_LETTERS.length; i++) {
  CP862[0x80 + i] = HEB_LETTERS[i];
}

function decodeVisual(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b >= 0x30 && b <= 0x39) out += String.fromCharCode(b);
    else if (b === 0x20) out += " ";
    else if (b >= 0x80 && CP862[b]) out += CP862[b];
    else out += " ";
  }
  return out;
}

function decodeHebField(bytes) {
  const raw = Array.from(bytes)
    .map(b => (b >= 0x80 && CP862[b]) ? CP862[b] : (b === 0x20 ? ' ' : (b >= 0x20 && b < 0x80 ? String.fromCharCode(b) : '')))
    .join('').trimStart();
  const reversed = [...raw].reverse().join('').trim();
  return reversed.replace(/[A-Za-z]+/g, w => [...w].reverse().join(''));
}

function reverseHeb(s) {
  return [...s.trim()].reverse().join("").trim();
}

function formatDate(d8) {
  if (!/^\d{8}$/.test(d8)) return null;
  const yyyy = d8.slice(0, 4), mm = d8.slice(4, 6), dd = d8.slice(6, 8);
  if (+dd < 1 || +dd > 31 || +mm < 1 || +mm > 12 || +yyyy < 1900 || +yyyy > 2100) return null;
  return `${dd}/${mm}/${yyyy}`; // DD/MM/YYYY
}

function detectStride(bytes) {
  for (let i = 50; i < Math.min(bytes.length, 10000); i++) {
    if (bytes[i] === 0x0D && bytes[i + 1] === 0x0A) return i + 2; // CRLF
    if (bytes[i] === 0x0A) return i + 1;                            // LF only
  }
  return 2001;
}

function parsePFile(content) {
  const bytes = new Uint8Array(content);
  if (bytes.length < 60) return new Map();

  const stride = detectStride(bytes);
  const lineLen = stride - (bytes[stride - 1] === 0x0A && bytes[stride - 2] === 0x0D ? 2 : 1);
  const map = new Map();

  for (let offset = 0; offset + lineLen <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + lineLen);

    // 0-8 is policy number
    const policyNum = String.fromCharCode(...line.subarray(0, 9));
    if (!/^\d+$/.test(policyNum)) continue;

    // Based on actual offset analysis:
    // 39-48: TAZ (9 digits)
    // 48-56: YYYYMMDD birth date
    // 56-96: Name
    
    const tazStr = String.fromCharCode(...line.subarray(39, 48));
    const tazMatch = tazStr.match(/(\d{8,9})/);
    let taz = tazMatch ? tazMatch[1].padStart(9, '0') : null;
    
    const birthDateStr = String.fromCharCode(...line.subarray(48, 56));
    const birthDate = formatDate(birthDateStr);
    
    let rawHebrew = Array.from(line.subarray(56, 96))
      .map(b => (b >= 0x80 && CP862[b]) ? CP862[b] : (b === 0x20 ? ' ' : (b >= 0x20 && b < 0x80 ? String.fromCharCode(b) : '')))
      .join('');
    
    // Take everything before a sequence of 2+ spaces, or before gender+0000 sequence
    rawHebrew = rawHebrew.split(/  +|[זנ\+]*0000/)[0];
    
    let rawName = [...rawHebrew].reverse().join('').trim();
    rawName = rawName.replace(/[A-Za-z]+/g, w => [...w].reverse().join(''));
    
    const nameParts = rawName.split(',').map(s => s.trim()).filter(Boolean);
    let lastName = '', firstName = '';
    if (nameParts.length >= 2) {
      lastName = nameParts[0];
      firstName = nameParts[1];
    } else {
      const parts = rawName.split(/\s+/).filter(Boolean);
      firstName = parts.pop() || '';
      lastName = parts.join(' ') || '';
    }

    if (!map.has(policyNum)) {
      map.set(policyNum, {
        policyNum,
        taz,
        firstName,
        lastName,
        birthDate
      });
    }
  }
  return map;
}

function parseNFile(content, pMap) {
  const bytes = new Uint8Array(content);
  if (bytes.length < 60) return [];

  const stride = detectStride(bytes);
  const lineLen = stride - (bytes[stride - 1] === 0x0A && bytes[stride - 2] === 0x0D ? 2 : 1);
  const coverages = [];

  for (let offset = 0; offset + lineLen <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + lineLen);

    const policyNum = String.fromCharCode(...line.subarray(0, 9));
    if (!/^\d+$/.test(policyNum)) continue;

    // In N file:
    // 0634435020260262100000010055000000000000021??~?00000000.00016019900000.00201602000000.00000000.00000036.52201601340690205++20150717%??%, ...
    // Coverage code/name is at bytes 43-47? "??~?"
    const coverageCode = String.fromCharCode(...line.subarray(43, 47));
    const coverageName = decodeHebField(line.subarray(43, 47));

    // Premium is around byte 100: "00000036.52" -> 36.52
    const premiumStr = String.fromCharCode(...line.subarray(91, 102)).trim();
    const monthlyPremium = parseFloat(premiumStr) || 0;
    
    // Look for start date YYYYMM around byte 102
    const startYm = String.fromCharCode(...line.subarray(102, 108));
    const startDate = /^\d{6}$/.test(startYm) ? `01/${startYm.slice(4,6)}/${startYm.slice(0,4)}` : null;

    // Look for TAZ in N file as well. Let's skip zeros just in case, or look specifically at 39-48 like P file.
    const tazStr = String.fromCharCode(...line.subarray(39, 48));
    const tazMatchN = tazStr.match(/(\d{8,9})/);
    const insuredTaz = tazMatchN ? tazMatchN[1].padStart(9, '0') : null;

    coverages.push({
      policyNum,
      coverageName: coverageName || 'כיסוי ביטוחי',
      coverageCode,
      monthlyPremium,
      startDate,
      insuredTaz
    });
  }
  return coverages;
}

function parseMOrGFile(content) {
  // G/M file contains balances for savings policies.
  // 0634435020260262100000010055000000000002606000000.00000000.00 ... 695.73
  const bytes = new Uint8Array(content);
  if (bytes.length < 60) return new Map();

  const stride = detectStride(bytes);
  const lineLen = stride - (bytes[stride - 1] === 0x0A && bytes[stride - 2] === 0x0D ? 2 : 1);
  const balances = new Map(); // policyNum -> balance

  for (let offset = 0; offset + lineLen <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + lineLen);

    const policyNum = String.fromCharCode(...line.subarray(0, 9));
    if (!/^\d+$/.test(policyNum)) continue;

    // Look for the largest floating point number in the line that could be a balance.
    // Menora typically places it around byte 110-150.
    const str = String.fromCharCode(...line);
    const f1 = parseFloat(str.substring(62, 81)) || 0;
    const f2 = parseFloat(str.substring(81, 95)) || 0;
    let balance = 0;
    if (!isNaN(f1) && !isNaN(f2)) {
      balance = Math.max(f1, f2);
    } else if (!isNaN(f1)) {
      balance = f1;
    } else if (!isNaN(f2)) {
      balance = f2;
    }
    
    // Ignore small fractional anomalies, require at least 1 Shekel
    if (balance >= 1) {
      const existing = balances.get(policyNum) || 0;
      balances.set(policyNum, existing + balance);
    }
  }
  return balances;
}

export function parseMenoraFiles(zipFiles) {
  const pEntry = zipFiles.find(f => f.name.toUpperCase().endsWith('P.TXT'));
  const nEntry = zipFiles.find(f => f.name.toUpperCase().endsWith('N.TXT'));
  const gEntry = zipFiles.find(f => f.name.toUpperCase().endsWith('G.TXT'));
  const mEntry = zipFiles.find(f => f.name.toUpperCase().endsWith('M.TXT'));

  if (!pEntry || !nEntry) {
    throw new Error('לא נמצאו קבצי מנורה מתאימים (חסר P.TXT או N.TXT)');
  }

  const pMap = parsePFile(pEntry.content);
  const coverages = parseNFile(nEntry.content, pMap);
  const balances = new Map();
  if (gEntry) {
    const gBals = parseMOrGFile(gEntry.content);
    for (const [k,v] of gBals) balances.set(k, (balances.get(k)||0) + v);
  }
  if (mEntry) {
    const mBals = parseMOrGFile(mEntry.content);
    for (const [k,v] of mBals) balances.set(k, (balances.get(k)||0) + v);
  }

  const membersMap = new Map(); // policyNum -> member object

  for (const [policyNum, pData] of pMap.entries()) {
    const balance = balances.get(policyNum) || 0;
    const policyCoverages = coverages.filter(c => c.policyNum === policyNum);
    const totalPremium = policyCoverages.reduce((sum, c) => sum + c.monthlyPremium, 0);

    // If it has a balance, it's a savings policy (פרט חיסכון)
    // If it has premiums but no balance, it's health (פרט בריאות)
    // Sometimes a policy has both, we'll split them or classify based on dominant trait.
    // To be safe, we create a member for Health if there are premiums > 0.
    // And a member for Savings if balance > 0.
    
    let taz = pData.taz;
    if (!taz && policyCoverages.length > 0) {
        taz = policyCoverages[0].insuredTaz;
    }
    if (!taz) taz = policyNum; // fallback

    if (balance > 0) {
      membersMap.set(`${policyNum}_savings`, {
        rawId: policyNum,
        taz,
        firstName: pData.firstName,
        lastName: pData.lastName,
        birthDate: pData.birthDate,
        startDate: policyCoverages[0]?.startDate,
        productType: 'savings',
        funds: [{
          name: 'פוליסת חיסכון',
          balance: balance
        }]
      });
    }

    if (totalPremium > 0 || balance === 0) {
      membersMap.set(`${policyNum}_health`, {
        rawId: policyNum,
        taz,
        firstName: pData.firstName,
        lastName: pData.lastName,
        birthDate: pData.birthDate,
        startDate: policyCoverages[0]?.startDate,
        productType: 'health',
        funds: policyCoverages.map(c => ({
          name: c.coverageName,
          balance: 0,
          monthlyPremium: c.monthlyPremium,
          startDate: c.startDate,
          insuredTaz: c.insuredTaz
        }))
      });
    }
  }

  return { members: Array.from(membersMap.values()) };
}
